import { json } from '@codemirror/lang-json';
import { closeLintPanel, diagnosticCount, linter, openLintPanel } from '@codemirror/lint';
import type { ReactCodeMirrorProps, ReactCodeMirrorRef } from '@uiw/react-codemirror';
import CodeMirror from '@uiw/react-codemirror';
import { Box, Button, Loader } from '@wix/design-system';
import { StatusAlertSmall } from '@wix/wix-ui-icons-common';
import lodash from 'lodash';
import React from 'react';

import type { RichContent } from './../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { EditorPluginName } from './../../../../ricos-schema/next-schema/index.js';
import { parseFromString } from './../../../../ricos-schema/next-schema/index.js';

import { fixDocument } from './autofix-api.js';
import { wixDocsCodeTheme } from './code-mirror-theme.js';

type RicosContentEditorTabProps = {
  editorPlugins: EditorPluginName[];
  content: RichContent;
  onChange: (content: RichContent) => void;
  mode: 'viewer' | 'editor';
};

export const RicosContentEditorTab: React.FC<RicosContentEditorTabProps> = ({
  editorPlugins,
  content,
  onChange,
  mode,
}) => {
  const [hasErrors, setHasErrors] = React.useState(false);
  const [fixPending, setFixPending] = React.useState(false);
  const codemirrorRef = React.useRef<ReactCodeMirrorRef>(null);
  const stringContent = JSON.stringify(content, null, 2);
  const isValidJson = getIsValidJson(stringContent);

  React.useEffect(() => {
    if (!hasErrors && codemirrorRef.current?.view) {
      closeLintPanel(codemirrorRef.current?.view);
    }
  }, [hasErrors]);

  const handleOnChange = React.useCallback(
    (value: string | RichContent) => {
      const parsedContent = typeof value === 'string' ? parseWithFallback(value, content) : value;
      onChange(parsedContent);
    },
    [content]
  );

  const onChangeDebounced = React.useCallback(lodash.debounce(handleOnChange, 1000), [
    handleOnChange,
  ]);

  return (
    <Box direction="vertical" paddingInline="8px" gap="5px">
      {codemirrorRef.current && hasErrors && (
        <Box align="right" gap="8px">
          {isValidJson && mode === 'editor' && (
            <Button
              size="small"
              skin="standard"
              priority="secondary"
              onClick={() => {
                if (fixPending) {
                  return;
                }
                const result = safeParse(stringContent);
                if (result.type === 'ok') {
                  setFixPending(true);
                  fixDocument(result.value, editorPlugins)
                    .then(content => {
                      handleOnChange(content);
                    })
                    .catch(e => {
                      console.error(e);
                    })
                    .finally(() => {
                      setFixPending(false);
                    });
                }
              }}
            >
              {fixPending && (
                <>
                  <Loader size="tiny" />
                  <span className="mr-2" />
                </>
              )}{' '}
              Auto-Fix
            </Button>
          )}
          <Button
            size="small"
            prefixIcon={<StatusAlertSmall />}
            skin="destructive"
            priority="primary"
            onClick={() => {
              codemirrorRef.current?.view && openLintPanel(codemirrorRef.current?.view);
            }}
          >
            Errors
          </Button>
        </Box>
      )}
      <CodeMirror
        ref={codemirrorRef}
        theme={wixDocsCodeTheme}
        value={stringContent}
        minHeight="200px"
        extensions={
          [json(), codemirrorValidationPlugin(editorPlugins)] as ReactCodeMirrorProps['extensions']
        }
        onChange={onChangeDebounced}
        onUpdate={updates => {
          const hasErrorsLatest = diagnosticCount(updates.state) > 0;
          if (hasErrorsLatest !== hasErrors) {
            setHasErrors(hasErrorsLatest);
          }
        }}
      />
    </Box>
  );
};

const parseWithFallback = (json: string, fallback: unknown) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return fallback;
  }
};

const getIsValidJson = (json: string) => {
  try {
    JSON.parse(json);
    return true;
  } catch (e) {
    return false;
  }
};

const safeParse = (json: string) => {
  try {
    return { type: 'ok' as const, value: JSON.parse(json) };
  } catch (e) {
    return { type: 'error' as const, error: e };
  }
};

const codemirrorValidationPlugin = (editorPlugins: EditorPluginName[]) => {
  // TODO: Implement validation for schema features by using editor plugin names
  return linter(view => {
    const result = parseFromString(view.state.doc.toString(), { plugins: editorPlugins });
    if (result.type === 'ok') {
      return [];
    }
    return result.messages.map(message => ({
      ...message,
      severity: 'error',
    }));
  });
};
